import {css, cx as addStyles} from '@emotion/css';
import {dateToIso, isoToDate, isValidItemPickupTime, setTimestamp} from '@remoov/functional';
import {Button, CalendarIcon, ChevronArrowDown, Modal, theme} from '@remoov/remoov-components';
import React, {ButtonHTMLAttributes, ForwardedRef, forwardRef, HTMLAttributes} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {ReturnModal} from './ExpiredBoard';

export type ItemReturnModalProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => unknown;
  address: string;
  returnModal: ReturnModal;
  setReturnModal: (newReturnModal: RecursivePartial<ReturnModal>) => unknown;
  submitReturnModal: () => unknown;
};

export const ItemReturnModal = ({
  isOpen,
  setIsOpen,
  address,
  returnModal,
  setReturnModal,
  submitReturnModal,
}: ItemReturnModalProps) => {
  const DateCustomInput = forwardRef(function DateCustomFunc(
    //@ts-ignore
    {value, onClick},
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    return (
      <DateInput onClick={onClick} ref={ref}>
        {value}
        <IconContainer>
          <CalendarIcon size='halfMedium' />
        </IconContainer>
      </DateInput>
    );
  });

  const TimeCustomInput = forwardRef(function TimeCustomFunc(
    //@ts-ignore
    {value, onClick},
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    return (
      <DateInput onClick={onClick} ref={ref} className={css({marginLeft: '40px'})}>
        {value}
        <IconContainer>
          <ChevronArrowDown size='small' />
        </IconContainer>
      </DateInput>
    );
  });

  return (
    <Modal
      title={`Item #${returnModal?.item?.id || 0} Return`}
      isOpen={isOpen}
      setModal={setIsOpen}
    >
      <Content>
        <Text>
          Item can be picked up during business hours from our warehouse located at{' '}
          <TextBold>{address}</TextBold>.
          <br /> Please select the date and time when you would like to pick up the item:
        </Text>
        <DateTimeContainer>
          <DatePicker
            selected={isoToDate(returnModal.date)}
            onChange={date => date && setReturnModal({date: dateToIso(date)})}
            customInput={<DateCustomInput />}
            filterDate={date =>
              isValidItemPickupTime(
                setTimestamp(dateToIso(date), {
                  hours: 16,
                  minutes: 0,
                  seconds: 0,
                }),
              )
            }
            popperPlacement='bottom'
          />
          <DatePicker
            selected={isoToDate(returnModal.date)}
            onChange={date => date && setReturnModal({date: dateToIso(date)})}
            customInput={<TimeCustomInput />}
            filterTime={date => isValidItemPickupTime(dateToIso(date))}
            showTimeSelect
            showTimeSelectOnly
            dateFormat='h:mm aa'
            popperPlacement='bottom'
          />
        </DateTimeContainer>
        <ButtonContainer>
          <Button buttonStyle='primary' onClick={submitReturnModal}>
            Confirm
          </Button>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};
const flexStartColumn = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});
const Content = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={flexStartColumn} {...args} />
);
const textBase = css({
  color: theme.colors.darkGray,
  fontSize: theme.font.sizes.small,
  fontFamily: theme.font.family,
  fontWeight: theme.font.light,
  textAlign: 'justify',
});
const Text = (args: HTMLAttributes<HTMLSpanElement>) => <span className={textBase} {...args} />;
const TextBold = (args: HTMLAttributes<HTMLSpanElement>) => (
  <span className={addStyles(textBase, css({fontWeight: theme.font.bold}))} {...args} />
);
const DateTimeContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginTop: theme.spacings.small,
    })}
    {...args}
  />
);
const display = css({display: 'flex', alignItems: 'center', justifyContent: 'space-between'});
const size = css({width: '240px', height: '53px'});
const outline = css({
  border: `1px solid ${theme.colors.darkBlue}`,
  padding: `${theme.spacings.xxsmall} ${theme.spacings.xsmall}`,
});
const inputText = css({color: theme.colors.darkBlue, fontWeight: theme.font.normal});
const cursor = css({cursor: 'pointer'});
const color = css({backgroundColor: theme.colors.white});
const DateInput = forwardRef(function DateInput(
  {className, ...rest}: ButtonHTMLAttributes<HTMLButtonElement>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      className={addStyles(display, size, outline, textBase, inputText, cursor, color, className)}
      ref={ref}
      {...rest}
    />
  );
});
const IconContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '60px',
      height: '53px',
      background: theme.colors.lightBlue,
      borderTop: `1px solid ${theme.colors.darkBlue}`,
      borderBottom: `1px solid ${theme.colors.darkBlue}`,
      marginRight: `-${theme.spacings.xsmall}`,
    })}
    {...args}
  />
);
const ButtonContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={css({margin: `${theme.spacings.large} auto 0`})} {...args} />
);
